/**
 * InstallAppNudge — bandeau discret qui pousse un compte connecté et pas
 * encore installé à ajouter Chouette à son écran d'accueil.
 *
 * Porté de memoneo (`frontend/src/components/InstallAppNudge.tsx`), avec
 * deux différences :
 *
 *  - Pas de champ serveur `pwa_installed_at` ici : cette colonne vit sur la
 *    table `users` de memoneo, partagée entre les deux produits. La pinger
 *    depuis Chouette éteindrait le nudge d'installation memoneo du même
 *    utilisateur et fausserait la stat « PWA installée » de l'admin
 *    memoneo. Le gate reste donc 100% client : `isStandalonePwa()` +
 *    l'historique de rejets en localStorage (lib/installNudge.ts), rien
 *    côté compte.
 *  - Monté DANS le composant `Chrome` de App.tsx, pas à la racine de
 *    l'app. `Chrome` n'enveloppe que Accueil / Catalogue / Pack / Histoire /
 *    Créer / Réglages (voir les <Route> de App.tsx) — ce qui exclut d'office
 *    /listen (lecteur plein écran), /kid (mode enfant : un enfant ne
 *    doit jamais voir une invite d'installation adulte),
 *    /generation/:jobId, et tout /login* & /auth/*, sans avoir à maintenir
 *    ici la liste de préfixes que la version memoneo doit maintenir (montée
 *    à la racine, elle doit exclure ces écrans une par une). La seule
 *    exclusion encore codée ci-dessous est /settings, où InstallAppCard
 *    tient déjà le même discours en entier — l'y répéter en bandeau serait
 *    redondant sur l'écran qui affiche déjà la carte permanente.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { X, Download } from "lucide-react";
import { Button } from "./ui";
import { useAuth } from "../lib/auth";
import { isStandalonePwa } from "../lib/platform";
import { isNative } from "../native/platform";
import {
  detectInstallPlatform, loadInstallNudgeDismissals, recordInstallNudgeDismissal,
  shouldShowInstallNudge,
} from "../lib/installNudge";
import { useDeferredInstallPrompt, triggerInstallPrompt } from "../lib/installPrompt";

export default function InstallAppNudge() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const deferredEvent = useDeferredInstallPrompt();

  // null tant que non lu — évite un flash "visible" avant que localStorage
  // ait pu être consulté.
  const [dismissals, setDismissals] = useState<number[] | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    setDismissals(loadInstallNudgeDismissals());
  }, []);

  const platform = useMemo(
    () => detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints),
    [],
  );

  // Sur un desktop sans `beforeinstallprompt` (Firefox, Safari macOS), il n'y
  // a rien à montrer : pas de flux natif, et aucune instruction manuelle
  // honnête à donner. Chrome/Edge desktop, eux, émettent l'événement — le
  // chemin en un clic reste donc disponible là où l'installation l'est
  // vraiment.
  const installable = !!deferredEvent || platform !== "desktop";

  const eligible =
    isAuthenticated
    && installable
    && !isStandalonePwa()
    && !isNative()
    && location.pathname !== "/settings";

  const visible =
    eligible
    && dismissals !== null
    && shouldShowInstallNudge({ now: Date.now(), dismissals });

  function dismiss(): void {
    setDismissals(recordInstallNudgeDismissal(Date.now()));
  }

  async function handleInstallClick(): Promise<void> {
    setInstalling(true);
    try {
      await triggerInstallPrompt();
    } finally {
      setInstalling(false);
    }
  }

  if (!visible) return null;

  // desktop n'atteint jamais cette branche sans deferredEvent (voir
  // `installable` ci-dessus), donc pas de troisième clé à gérer ici.
  const stepsKey = platform === "ios" ? "install.nudge.iosSteps" : "install.nudge.androidSteps";

  return (
    <div
      role="status"
      className="install-nudge-offset fixed inset-x-3 sm:inset-x-auto sm:right-4 sm:w-80 z-40 rounded-card border border-line bg-surface overprint ink-line shadow-solid-sm p-4 flex flex-col gap-2"
    >
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm font-body text-paper leading-snug">
          {t("install.nudge.text")}
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("install.nudge.dismiss")}
          className="tap-target shrink-0 text-violet-muted hover:text-paper -mt-0.5 -mr-0.5"
        >
          <X size={16} aria-hidden />
        </button>
      </div>

      {!deferredEvent && (
        <p className="text-xs text-violet-soft font-body leading-snug">{t(stepsKey)}</p>
      )}

      {deferredEvent && (
        <Button
          onClick={() => void handleInstallClick()}
          disabled={installing}
          className="w-full sm:w-auto text-xs px-4 py-1.5"
        >
          <Download size={14} />
          {t("install.nudge.cta")}
        </Button>
      )}
    </div>
  );
}
